import { useState, useCallback, useMemo } from 'react';
import type { StackedChartDataPoint, ChartMetricConfig } from '../../../types';

export interface ZoomDomain {
  x: [number, number];
  y: Record<string, [number, number]>;
}

export interface SelectionRange {
  start: number;
  end: number;
}

export interface MetricAverage {
  metricId: string;
  average: number;
  min: number;
  max: number;
  count: number;
}

interface UseChartZoomProps {
  chartDataSets: StackedChartDataPoint[][];
  selectedMetricConfigs: ChartMetricConfig[];
  xAxisType: 'time' | 'distance';
  originalXDomain: [number, number];
}

type BoundaryLockState = 'none' | 'left-locked' | 'both-locked';

/**
 * Custom hook for managing chart zoom with slider-based selection
 */
export const useChartZoom = ({
  chartDataSets,
  selectedMetricConfigs,
  xAxisType,
  originalXDomain,
}: UseChartZoomProps) => {
  const [zoomDomain, setZoomDomain] = useState<ZoomDomain | null>(null);
  const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(null);

  // Slider mode states
  const [isZoomModeActive, setIsZoomModeActive] = useState(false);
  const [leftSlider, setLeftSlider] = useState<number>(originalXDomain[0]);
  const [rightSlider, setRightSlider] = useState<number>(originalXDomain[1]);
  const [boundaryLock, setBoundaryLock] = useState<BoundaryLockState>('none');

  const xKey = xAxisType === 'time' ? 'time' : 'distance';

  /**
   * Toggle zoom mode on/off
   */
  const toggleZoomMode = useCallback(() => {
    if (isZoomModeActive) {
      // Exiting zoom mode - reset everything
      setIsZoomModeActive(false);
      setLeftSlider(originalXDomain[0]);
      setRightSlider(originalXDomain[1]);
      setBoundaryLock('none');
      setZoomDomain(null);
      setSelectionRange(null);
    } else {
      // Entering zoom mode
      setIsZoomModeActive(true);
      setLeftSlider(originalXDomain[0]);
      setRightSlider(originalXDomain[1]);
      setBoundaryLock('none');
    }
  }, [isZoomModeActive, originalXDomain]);

  /**
   * Handle chart click in zoom mode to lock boundaries
   */
  const handleChartClick = useCallback((e: any) => {
    if (!isZoomModeActive) return;

    const clickedValue = e?.activeLabel;
    if (clickedValue === undefined) return;

    if (boundaryLock === 'none') {
      // First click - lock left boundary
      setLeftSlider(clickedValue);
      setBoundaryLock('left-locked');
    } else if (boundaryLock === 'left-locked') {
      // Second click - lock right boundary and apply zoom
      const left = Math.min(leftSlider, clickedValue);
      const right = Math.max(leftSlider, clickedValue);

      // Minimum selection threshold
      const minSelectionWidth = (originalXDomain[1] - originalXDomain[0]) * 0.02;
      if (right - left < minSelectionWidth) {
        // Too small, reset
        setBoundaryLock('none');
        setLeftSlider(originalXDomain[0]);
        setRightSlider(originalXDomain[1]);
        return;
      }

      setRightSlider(clickedValue);
      setBoundaryLock('both-locked');
      applyZoom(left, right);
    }
  }, [isZoomModeActive, boundaryLock, leftSlider, originalXDomain]);

  /**
   * Handle mouse move to update slider position (before locking)
   */
  const handleMouseMove = useCallback((e: any) => {
    if (!isZoomModeActive) return;

    const hoverValue = e?.activeLabel;
    if (hoverValue === undefined) return;

    if (boundaryLock === 'none') {
      // Before first lock, left slider follows mouse
      setLeftSlider(hoverValue);
    } else if (boundaryLock === 'left-locked') {
      // After left lock, right slider follows mouse
      setRightSlider(hoverValue);
    }
  }, [isZoomModeActive, boundaryLock]);

  /**
   * Apply zoom with the selected range
   */
  const applyZoom = useCallback((left: number, right: number) => {
    // Calculate new Y domains for each metric
    const newYDomains: Record<string, [number, number]> = {};

    chartDataSets.forEach((dataset, index) => {
      const metricConfig = selectedMetricConfigs[index];
      if (!metricConfig) return;

      const filteredData = dataset.filter(
        (point) => point[xKey] >= left && point[xKey] <= right
      );

      if (filteredData.length > 0) {
        const values = filteredData.map((d) => d.value).filter((v) => isFinite(v));
        if (values.length > 0) {
          const min = Math.min(...values);
          const max = Math.max(...values);

          // Add 5% padding
          const padding = (max - min) * 0.05;
          const paddedMin = metricConfig.invertAxis ? min : Math.max(0, min - padding);
          const paddedMax = max + padding;

          newYDomains[metricConfig.id] = [paddedMin, paddedMax];
        }
      }
    });

    setZoomDomain({
      x: [left, right],
      y: newYDomains,
    });

    setSelectionRange({ start: left, end: right });
  }, [chartDataSets, selectedMetricConfigs, xKey]);

  /**
   * Calculate averages for the selected range
   */
  const selectedRangeAverages = useMemo(() => {
    if (!selectionRange) return null;

    const averages: MetricAverage[] = [];

    chartDataSets.forEach((dataset, index) => {
      const metricConfig = selectedMetricConfigs[index];
      if (!metricConfig) return;

      const filteredData = dataset.filter(
        (point) => point[xKey] >= selectionRange.start && point[xKey] <= selectionRange.end
      );

      if (filteredData.length > 0) {
        const values = filteredData.map((d) => d.value).filter((v) => isFinite(v));

        if (values.length > 0) {
          const sum = values.reduce((acc, val) => acc + val, 0);
          const average = sum / values.length;
          const min = Math.min(...values);
          const max = Math.max(...values);

          averages.push({
            metricId: metricConfig.id,
            average,
            min,
            max,
            count: values.length,
          });
        }
      }
    });

    return averages.length > 0 ? averages : null;
  }, [selectionRange, chartDataSets, selectedMetricConfigs, xKey]);

  /**
   * Reset zoom to original view
   */
  const resetZoom = useCallback(() => {
    setZoomDomain(null);
    setSelectionRange(null);
    setLeftSlider(originalXDomain[0]);
    setRightSlider(originalXDomain[1]);
    setBoundaryLock('none');
    setIsZoomModeActive(false);
  }, [originalXDomain]);

  /**
   * Get effective X domain (zoomed or original)
   */
  const effectiveXDomain = useMemo(() => {
    return zoomDomain?.x || originalXDomain;
  }, [zoomDomain, originalXDomain]);

  /**
   * Get effective Y domain for a specific metric (zoomed or original)
   */
  const getEffectiveYDomain = useCallback((metricId: string, originalYDomain: [number, number]) => {
    return zoomDomain?.y[metricId] || originalYDomain;
  }, [zoomDomain]);

  /**
   * Get average for a specific metric
   */
  const getMetricAverage = useCallback((metricId: string): number | null => {
    if (!selectedRangeAverages) return null;
    const metricAvg = selectedRangeAverages.find((avg) => avg.metricId === metricId);
    return metricAvg?.average ?? null;
  }, [selectedRangeAverages]);

  const isZoomed = zoomDomain !== null;

  // Get display positions for sliders
  const sliderLeft = Math.min(leftSlider, rightSlider);
  const sliderRight = Math.max(leftSlider, rightSlider);

  return {
    // State
    isZoomed,
    isZoomModeActive,
    boundaryLock,

    // Slider positions
    sliderLeft,
    sliderRight,

    // Domains
    effectiveXDomain,
    getEffectiveYDomain,

    // Averages
    selectedRangeAverages,
    getMetricAverage,

    // Handlers
    toggleZoomMode,
    handleChartClick,
    handleMouseMove,
    resetZoom,
  };
};
